import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useKbArticle } from '@/queries/kb'
import { api } from '@/lib/api'
import { kbImageApiPath } from './kbImagePath'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@/components/ui/button'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { formatDate } from '@/lib/dates'
import { KbRestrictedBadge } from './KbRestrictedBadge'
import { KbArticleContentsInline, KbArticleContentsRail } from './KbArticleContents'
import { remarkContents, sameContents, type ContentsEntry } from './articleContents'

// With contents the page widens past AppShell's 1080px column to 1200px: the extra room is the rail's, and the text
// stays at reading width. Negative margins centred on <main>, which is itself centred, never past a 2rem gutter.
const WIDE_PAGE: CSSProperties = { marginInline: 'calc((100% - min(1200px, 100vw - 4rem)) / 2)' }

function HeadingAnchor({ id, children }: { id?: string; children?: ReactNode }) {
  return (
    <>
      {children}
      {id && (
        <a
          href={`#${id}`}
          aria-label="Link to this section"
          className="ml-2 font-normal text-etn-text-soft no-underline opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        >
          #
        </a>
      )}
    </>
  )
}

const headingComponents = {
  h2: ({ id, children }: { id?: string; children?: ReactNode }) => (
    <h2 id={id} className="group scroll-mt-6">
      <HeadingAnchor id={id}>{children}</HeadingAnchor>
    </h2>
  ),
  h3: ({ id, children }: { id?: string; children?: ReactNode }) => (
    <h3 id={id} className="group scroll-mt-6">
      <HeadingAnchor id={id}>{children}</HeadingAnchor>
    </h3>
  ),
}

function KbInlineImage({ src, alt }: { src?: string; alt?: string }) {
  const [resolved, setResolved] = useState<string | null>(null)
  const apiPath = kbImageApiPath(src)

  if (!apiPath) {
    return <img src={src} alt={alt ?? ''} className="rounded border border-border max-h-[480px]" />
  }
  if (!resolved) {
    // Through the authed client: the API only serves an image the reader has an article for.
    api<{ url?: string }>(apiPath, { query: { json: 1 } })
      .then(j => { if (j?.url) setResolved(j.url) })
      .catch(() => undefined)
    return <span className="text-xs text-muted-foreground">{alt ?? 'image'} (loading…)</span>
  }
  return <img src={resolved} alt={alt ?? ''} className="rounded border border-border max-h-[480px]" />
}

export function KbArticleView({ slug }: { slug: string }) {
  const { data, isLoading, error } = useKbArticle(slug)
  const { user } = useAuth0()
  const isAdmin = Boolean((user as Record<string, unknown> | undefined)?.['https://app.etainabl.com/isAdmin'])

  // The markdown renderer reports the headings as it renders; the layout effect reads them before paint, so the rail
  // appears with the article rather than a frame after it.
  const reported = useRef<ContentsEntry[]>([])
  const [contents, setContents] = useState<ContentsEntry[]>([])
  const remarkPlugins = useMemo(() => [remarkGfm, [remarkContents, (entries: ContentsEntry[]) => (reported.current = entries)]], [])
  useLayoutEffect(() => {
    setContents(prev => (sameContents(prev, reported.current) ? prev : reported.current))
  })
  // A link to a section (/kb/<slug>#quick-fixes) lands before the article has loaded, so the browser can't scroll to it.
  // Tracked per article: moving to another article in the same view should land on its section too.
  const landedOn = useRef<string | undefined>(undefined)
  useLayoutEffect(() => {
    if (landedOn.current === slug || !contents.length || !window.location.hash) return
    let id: string
    try {
      id = decodeURIComponent(window.location.hash.slice(1))
    } catch {
      return
    }
    const target = document.getElementById(id)
    if (!target) return
    landedOn.current = slug
    target.scrollIntoView({ block: 'start' })
  }, [contents, slug])
  const showContents = contents.length >= 2

  if (isLoading) {
    return <FullPageSpinner />
  }

  if (error || !data) {
    return (
      <div className="container mx-auto max-w-3xl p-6">
        <p className="text-sm text-muted-foreground mb-4">Article not found.</p>
        <Link to="/kb"><Button variant="outline" size="sm"><ArrowLeft className="mr-1.5 h-4 w-4" />Back to Knowledgebase</Button></Link>
      </div>
    )
  }

  return (
    <div style={showContents ? WIDE_PAGE : undefined} className={showContents ? 'py-6' : 'container mx-auto max-w-3xl p-6'}>
      <nav aria-label="Breadcrumb" className="mb-6">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
          <li>
            <Link to="/kb" className="inline-flex items-center gap-1.5 hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" />
              Knowledgebase
            </Link>
          </li>
          {data.collection && (
            <>
              <li aria-hidden><ChevronRight className="h-3.5 w-3.5" /></li>
              <li>
                <Link to={`/kb/c/${data.collection.slug}` as never} className="hover:text-foreground">
                  {data.collection.name}
                </Link>
              </li>
            </>
          )}
        </ol>
      </nav>

      <div className={showContents ? 'lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:items-start lg:gap-14' : undefined}>
        {showContents && <KbArticleContentsRail entries={contents} />}
        <article className="min-w-0 max-w-[720px]">
          {!data.isPublished && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 mb-4">
              This article is a draft. Only admins can see it.
            </div>
          )}

          <header className="mb-6 space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">{data.title}</h1>
            {data.subtitle && <p className="text-lg text-muted-foreground">{data.subtitle}</p>}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Updated {formatDate(data.updatedAt)}
              {isAdmin && <KbRestrictedBadge article={data} detailed />}
            </div>
          </header>

          {showContents && <KbArticleContentsInline entries={contents} />}

          <div className="prose prose-sm sm:prose max-w-none">
            <ReactMarkdown remarkPlugins={remarkPlugins as never} components={{ img: KbInlineImage as never, ...headingComponents }}>
              {data.content || '*No content yet.*'}
            </ReactMarkdown>
          </div>

          {isAdmin && (
            <div className="mt-8 pt-6 border-t">
              <Link to={`/admin/kb/${data._id}` as never}>
                <Button size="sm" variant="outline">Edit article</Button>
              </Link>
            </div>
          )}
        </article>
      </div>
    </div>
  )
}
